import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setRouteType,
    selectRouteType,
    setWizardStepCompleted
} from '../../../features/tripcenter/googleRouteMakerSlice';

// MUI components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';

// MUI icons
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// Simple diagrams with icons in avatar circles
const OneWayDiagram = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 2, gap: 2 }}>
        <Avatar sx={{ bgcolor: '#e3f2fd', width: 40, height: 40 }}>
            <LocationOnIcon sx={{ color: '#1E90FF' }} />
        </Avatar>

        <Box sx={{
            position: 'relative',
            mx: 1,
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            //marginLeft: 5,
            minWidth: 25
        }}>
            <Box sx={{
                height: '3px',
                bgcolor: '#1976d2',
                width: '100%',
                opacity: 0.5
            }} />
            <ArrowRightAltIcon
                sx={{
                    position: 'absolute',
                    right: -5,
                    color: '#1976d2',
                    fontSize: 28
                }}
            />
        </Box>

        <Avatar sx={{ bgcolor: '#e8eaf6', width: 40, height: 40 }}>
            <LocationOnIcon sx={{ color: 'crimson' }} />
        </Avatar>
    </Box>
);

const RoundTripDiagram = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 2, gap: 2 }}>
        <Avatar sx={{ bgcolor: '#e3f2fd', width: 40, height: 40 }}>
            <LocationOnIcon sx={{ color: '#1E90FF' }} />
        </Avatar>

        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mx: 1.5,
            flex: 1,
            minWidth: 25
        }}>

            <Box sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                mb: 0.5
            }}>
                <Box sx={{
                    height: '2px',
                    bgcolor: '#1976d2',
                    width: '100%',
                    opacity: 0.5
                }} />
                <ArrowRightAltIcon
                    sx={{
                        position: 'absolute',
                        right: -5,
                        color: '#1976d2',
                        fontSize: 24
                    }}
                />
            </Box>

            <Box sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                mt: 0.5
            }}>
                <ArrowRightAltIcon
                    sx={{
                        position: 'absolute',
                        left: -5,
                        color: '#1976d2',
                        fontSize: 24,
                        transform: 'rotate(180deg)'
                    }}
                />
                <Box sx={{
                    height: '2px',
                    bgcolor: '#1976d2',
                    width: '100%',
                    opacity: 0.5
                }} />
            </Box>
        </Box>

        <Avatar sx={{ bgcolor: '#e8eaf6', width: 40, height: 40 }}>
            <LocationOnIcon sx={{ color: 'crimson' }} />
        </Avatar>
    </Box>
);

const OneWayWithStopsDiagram = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 2, gap: 2 }}>
        <Avatar sx={{ bgcolor: '#e3f2fd', width: 40, height: 40 }}>
            <LocationOnIcon sx={{ color: '#1E90FF' }} />
        </Avatar>

        <Box sx={{
            position: 'relative',
            mx: 1,
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            //marginLeft: 5,
            minWidth: 10
        }}>
            <Box sx={{
                height: '3px',
                bgcolor: '#1976d2',
                width: '100%',
                opacity: 0.5
            }} />
            <ArrowRightAltIcon
                sx={{
                    position: 'absolute',
                    right: -5,
                    color: '#1976d2',
                    fontSize: 28
                }}
            />
        </Box>
        <Avatar sx={{ bgcolor: '#fce4ec', width: 24, height: 24 }}>
            <FiberManualRecordIcon sx={{ color: 'orchid', fontSize: 14 }} />
        </Avatar>
        <Box sx={{
            position: 'relative',
            mx: 1,
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            //marginLeft: 5,
            minWidth: 10
        }}>
            <Box sx={{
                height: '3px',
                bgcolor: '#1976d2',
                width: '100%',
                opacity: 0.5
            }} />
            <ArrowRightAltIcon
                sx={{
                    position: 'absolute',
                    right: -5,
                    color: '#1976d2',
                    fontSize: 28
                }}
            />
        </Box>

        <Avatar sx={{ bgcolor: '#e8eaf6', width: 40, height: 40 }}>
            <LocationOnIcon sx={{ color: 'crimson' }} />
        </Avatar>
    </Box>
);


const RoundTripWithStopsDiagram = () => (
    <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 1,
        my: 2
    }}>
        {/* Top row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: '#e3f2fd', width: 36, height: 36 }}>
                <LocationOnIcon sx={{ color: '#1E90FF' }} />
            </Avatar>

            <ArrowRightAltIcon sx={{ color: '#1976d2', fontSize: 24 }} />

            <Avatar sx={{ bgcolor: '#fce4ec', width: 24, height: 24 }}>
                <FiberManualRecordIcon sx={{ color: 'orchid', fontSize: 14 }} />
            </Avatar>

            <ArrowRightAltIcon sx={{ color: '#1976d2', fontSize: 24 }} />

            <Avatar sx={{ bgcolor: '#fce4ec', width: 24, height: 24 }}>
                <FiberManualRecordIcon sx={{ color: 'orchid', fontSize: 14 }} />
            </Avatar>
        </Box>

        {/* Middle row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mx: 0.5 }}>
            <ArrowRightAltIcon
                sx={{
                    color: '#1976d2',
                    fontSize: 24,
                    transform: 'rotate(270deg)'
                }}
            />

            <Box sx={{ flex: 1 }} /> {/* Empty space */}
            <Box sx={{ flex: 1 }} /> {/* Empty space */}
            <Box sx={{ flex: 1 }} /> {/* Empty space */}

            <ArrowRightAltIcon
                sx={{
                    color: '#1976d2',
                    fontSize: 24,
                    transform: 'rotate(90deg)'
                }}
            />
        </Box>

        {/* Bottom row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: '#fce4ec', width: 24, height: 24 }}>
                <FiberManualRecordIcon sx={{ color: 'orchid', fontSize: 14 }} />
            </Avatar>

            <ArrowRightAltIcon
                sx={{
                    color: '#1976d2',
                    fontSize: 24,
                    transform: 'rotate(180deg)'
                }}
            />

            <Avatar sx={{ bgcolor: '#fce4ec', width: 24, height: 24 }}>
                <FiberManualRecordIcon sx={{ color: 'orchid', fontSize: 14 }} />
            </Avatar>

            <ArrowRightAltIcon
                sx={{
                    color: '#1976d2',
                    fontSize: 24,
                    transform: 'rotate(180deg)'
                }}
            />

            <Avatar sx={{ bgcolor: '#fce4ec', width: 24, height: 24 }}>
                <FiberManualRecordIcon sx={{ color: 'orchid', fontSize: 14 }} />
            </Avatar>
        </Box>
    </Box>
);

// Route type definitions
const routeTypes = [
    {
        id: 'oneWayDirect',
        title: 'One Way Direct',
        description: 'Location A to Location B direct route. No stops. Trip ends at end of route.',
        illustration: <OneWayDiagram />
    },
    {
        id: 'roundTrip',
        title: 'Round Trip',
        description: 'Location A to B and back to A. Trip ends after vehicle is back to Location A.',
        illustration: <RoundTripDiagram />
    },
    {
        id: 'oneWayWithStops',
        title: 'One Way with Stops',
        description: 'Start at Location A, go to multiple locations on the way, and end at a final destination. Trip ends at final destination',
        illustration: <OneWayWithStopsDiagram />
    },
    {
        id: 'roundTripWithStops',
        title: 'Round Trip with Stops',
        description: 'Start at Location A, go to multiple locations along the way, and finally return to the starting point. Trip ends after vehicle is back to Location A.',
        illustration: <RoundTripWithStopsDiagram />
    }
];

const RouteTypeSelector = () => {
    const dispatch = useDispatch();
    const selectedRouteType = useSelector(selectRouteType);

    const handleSelectRouteType = (routeTypeId) => {
        dispatch(setRouteType(routeTypeId));
        dispatch(setWizardStepCompleted({ step: 'routeType', completed: true }));
    };

    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom fontWeight="500" color="primary.dark">
                Select Route Type
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
                Choose the type of route you want to create. This will determine what location information you'll need to provide in the next steps.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
                {routeTypes.map((routeType) => (
                    <Grid item xs={12} md={6} key={routeType.id}>
                        <Card
                            variant="outlined"
                            sx={{
                                height: '100%',
                                borderRadius: 2,
                                borderColor: selectedRouteType === routeType.id ? 'primary.main' : 'divider',
                                borderWidth: selectedRouteType === routeType.id ? 2 : 1,
                                boxShadow: selectedRouteType === routeType.id ? 3 : 0,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    boxShadow: 2,
                                    transform: 'translateY(-4px)'
                                }
                            }}
                        >
                            <CardActionArea
                                onClick={() => handleSelectRouteType(routeType.id)}
                                sx={{ height: '100%', p: 1 }}
                            >
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {routeType.title}
                                    </Typography>

                                    <Box sx={{
                                        backgroundColor: selectedRouteType === routeType.id ? 'primary.50' : 'grey.50',
                                        borderRadius: 2,
                                        p: 2,
                                            my: 2,
                                        minHeight: 80,
                                        display: 'flex',
                                            alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {routeType.illustration}
                                    </Box>

                                    <Typography variant="body2" color="text.secondary">
                                        {routeType.description}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {selectedRouteType && (
                <Box mt={4} textAlign="center">
                    <Typography variant="body1" fontWeight="medium">
                        You've selected <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                            {routeTypes.find(rt => rt.id === selectedRouteType)?.title}
                        </span>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Click "Next" to proceed to location setup.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default RouteTypeSelector; 